<script setup>
import { reactiveOmit } from '@vueuse/core';
import { Maximize2, Minimize2, X } from 'lucide-vue-next';
import { ref } from 'vue';
import {
  DialogClose,
  DialogContent,
  DialogPortal,
  useForwardPropsEmits,
} from 'reka-ui';
import { cn } from '@/lib/utils';
import DialogOverlay from './DialogOverlay.vue';

const props = defineProps({
  forceMount: { type: Boolean, required: false },
  trapFocus: { type: Boolean, required: false },
  disableOutsidePointerEvents: { type: Boolean, required: false },
  asChild: { type: Boolean, required: false },
  as: { type: [String, Object, Function], required: false },
  class: { type: null, required: false },
  expandable: { type: Boolean, required: false, default: false },
});
const emits = defineEmits([
  'escapeKeyDown',
  'pointerDownOutside',
  'focusOutside',
  'interactOutside',
  'openAutoFocus',
  'closeAutoFocus',
]);

const isExpanded = ref(false);

const delegatedProps = reactiveOmit(props, 'class', 'expandable');

const forwarded = useForwardPropsEmits(delegatedProps, emits);

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value;
};

const headerControlClass =
  "ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4";
</script>

<template>
  <DialogPortal>
    <DialogOverlay />
    <DialogContent
      data-slot="dialog-content"
      v-bind="forwarded"
      :class="
        cn(
          'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200 sm:max-w-lg',
          props.class,
          isExpanded &&
            '!h-[calc(100%-2rem)] !max-h-none !min-h-0 !w-[calc(100%-2rem)] !max-w-none',
        )
      "
    >
      <slot />

      <button
        v-if="expandable"
        type="button"
        :class="cn(headerControlClass, 'right-12')"
        @click="toggleExpanded"
      >
        <Minimize2 v-if="isExpanded" />
        <Maximize2 v-else />
        <span class="sr-only">{{ isExpanded ? 'Restaurar' : 'Ampliar' }}</span>
      </button>

      <DialogClose
        :class="cn(headerControlClass, 'right-4')"
      >
        <X />
        <span class="sr-only">Close</span>
      </DialogClose>
    </DialogContent>
  </DialogPortal>
</template>
